import React, { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, CloudArrowUpIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const BlogModal = ({ isOpen, onClose, onSubmit, post, loading }) => {
    const [formData, setFormData] = useState({
        title: '',
        excerpt: '',
        long_description: '',
        highlights: [''],
        category: 'Home Construction Loan',
        author: '',
        date: '',
        read_time: '',
        image_url: '',
        featured: false,
        display_order: 0,
        is_active: true
    });

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const categories = [
        'Home Construction Loan',
        'Home Purchase or Resale Loan',
        'Home Affordable Loan',
        'PMAY Urban 2.0',
        'Composite Loan',
        'Home Renovation Loan',
        'Home Extension Loan',
        'Balance Transfer Loan',
        'NRI Home Loan',
        'Top-up Loan',
        'Mortgage Loan'
    ];

    useEffect(() => {
        if (post) {
            setFormData({
                title: post.title || '',
                excerpt: post.excerpt || '',
                long_description: (() => {
                    if (!post.long_description) return '';
                    if (Array.isArray(post.long_description)) return post.long_description.map(p => `<p>${p}</p>`).join('');
                    if (typeof post.long_description === 'string') {
                        try {
                            const parsed = JSON.parse(post.long_description);
                            if (Array.isArray(parsed)) return parsed.map(p => `<p>${p}</p>`).join('');
                            return post.long_description;
                        } catch (e) {
                            return post.long_description;
                        }
                    }
                    return '';
                })(),
                highlights: Array.isArray(post.highlights)
                    ? post.highlights
                    : (typeof post.highlights === 'string' ? JSON.parse(post.highlights) : ['']),
                category: post.category || 'Home Construction Loan',
                author: post.author || '',
                date: post.date || '',
                read_time: post.read_time || '',
                image_url: post.image_url || '',
                featured: post.featured ?? false,
                display_order: post.display_order || 0,
                is_active: post.is_active ?? true
            });

            if (post.image_url) {
                const imageUrl = post.image_url.startsWith('http')
                    ? post.image_url
                    : `/${post.image_url.startsWith('/') ? post.image_url.slice(1) : post.image_url}`;
                setImagePreview(imageUrl);
            } else {
                setImagePreview(null);
            }
            setImageFile(null);
        } else {
            setFormData({
                title: '',
                excerpt: '',
                long_description: '',
                highlights: [''],
                category: 'Home Construction Loan',
                author: '',
                date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
                read_time: '',
                image_url: '',
                featured: false,
                display_order: 0,
                is_active: true
            });
            setImagePreview(null);
            setImageFile(null);
        }
    }, [post, isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.match(/^image\/(webp|png)$/)) {
                alert('Only WebP and PNG formats are allowed');
                return;
            }
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageFile(file);
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleArrayChange = (index, value, field) => {
        const newArray = [...formData[field]];
        newArray[index] = value;
        setFormData(prev => ({ ...prev, [field]: newArray }));
    };

    const addArrayItem = (field) => {
        setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));
    };

    const removeArrayItem = (index, field) => {
        const newArray = formData[field].filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, [field]: newArray.length > 0 ? newArray : [''] }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const submitData = new FormData();
        submitData.append('title', formData.title);
        submitData.append('excerpt', formData.excerpt);
        submitData.append('category', formData.category);
        submitData.append('author', formData.author);
        submitData.append('date', formData.date);
        submitData.append('read_time', formData.read_time);
        submitData.append('featured', formData.featured ? '1' : '0');
        submitData.append('display_order', formData.display_order);
        submitData.append('is_active', formData.is_active ? '1' : '0');
        submitData.append('long_description', formData.long_description);
        submitData.append('highlights', JSON.stringify(formData.highlights));

        // Only append image_url if we are NOT uploading a new file (to keep existing one)
        // If uploading new, backend ignores this and uses the file
        if (!imageFile) {
            submitData.append('image_url', formData.image_url);
        }

        if (imageFile) {
            submitData.append('image', imageFile);
        }

        onSubmit(submitData);
    };

    return (
        <div className="fixed inset-0 z-[100] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity" aria-hidden="true" onClick={onClose}>
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"></div>
                </div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-3xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    <div className="bg-white px-8 pt-8 pb-8">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <h3 className="text-2xl font-bold text-slate-900">
                                    {post ? 'Edit Blog Post' : 'Add New Blog Post'}
                                </h3>
                                <p className="text-slate-500 mt-1">
                                    {post ? 'Update post details and content.' : 'Enter details for the new blog post.'}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-slate-100 rounded-xl text-slate-400 hover:text-slate-600 transition-colors"
                            >
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Title */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Post Title</label>
                                    <input
                                        type="text"
                                        name="title"
                                        required
                                        value={formData.title}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                                        placeholder="Enter a compelling title..."
                                    />
                                </div>

                                {/* Excerpt */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Short Excerpt</label>
                                    <textarea
                                        name="excerpt"
                                        required
                                        rows="2"
                                        value={formData.excerpt}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none resize-none"
                                        placeholder="A brief summary for the blog grid..."
                                    ></textarea>
                                </div>

                                {/* Category & Author */}
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Category</label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                                    >
                                        {categories.map(cat => (
                                            <option key={cat} value={cat}>{cat}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Author Name</label>
                                    <input
                                        type="text"
                                        name="author"
                                        required
                                        value={formData.author}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                                        placeholder="e.g., Rajesh Kumar"
                                    />
                                </div>

                                {/* Date & Read Time */}
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Publish Date</label>
                                    <input
                                        type="text"
                                        name="date"
                                        required
                                        value={formData.date}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                                        placeholder="e.g., Jan 15, 2025"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Read Time</label>
                                    <input
                                        type="text"
                                        name="read_time"
                                        required
                                        value={formData.read_time}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                                        placeholder="e.g., 8 min read"
                                    />
                                </div>

                                {/* Image Upload */}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-bold text-slate-700 mb-2">
                                        Blog Image <span className="text-slate-400">(Optional)</span>
                                    </label>
                                    <p className="text-xs text-slate-500 mb-3">
                                        📐 Recommended: 1600 x 800 pixels | Formats: WebP, PNG only
                                    </p>

                                    <div className="flex gap-4">
                                        {/* Upload Area */}
                                        <div className="flex-1">
                                            <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-primary-500 transition-colors bg-slate-50 hover:bg-white">
                                                <input
                                                    type="file"
                                                    accept=".webp,.png,image/webp,image/png"
                                                    onChange={handleImageChange}
                                                    className="hidden"
                                                />
                                                <CloudArrowUpIcon className="h-12 w-12 text-slate-400 mx-auto mb-2" />
                                                <p className="text-sm font-medium text-slate-600">Change Image</p>
                                                <p className="text-xs text-slate-400 mt-1">WebP or PNG, max 5MB</p>
                                            </label>
                                        </div>

                                        {/* Image Preview */}
                                        {imagePreview && (
                                            <div className="w-48 h-32 rounded-xl overflow-hidden border-2 border-slate-200 bg-slate-50">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Featured & Status */}
                                <div className="flex items-center gap-8 md:col-span-2 flex-wrap">
                                    <label className="flex items-center cursor-pointer">
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                name="is_active"
                                                checked={formData.is_active}
                                                onChange={handleChange}
                                                className="sr-only"
                                            />
                                            <div className={`w-12 h-6 rounded-full transition-colors ${formData.is_active ? 'bg-primary-600' : 'bg-slate-300'}`}></div>
                                            <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.is_active ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                        </div>
                                        <span className="ml-3 text-sm font-bold text-slate-700">Active Status</span>
                                    </label>

                                    <div className="flex-1 flex items-center justify-end gap-2 text-sm font-bold text-slate-700">
                                        <span>Display Order:</span>
                                        <input
                                            type="number"
                                            name="display_order"
                                            value={formData.display_order}
                                            onChange={handleChange}
                                            className="w-20 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-primary-500"
                                        />
                                    </div>
                                </div>

                                {/* Long Description (CKEditor) */}
                                <div className="md:col-span-2 space-y-3">
                                    <label className="text-sm font-bold text-slate-700">Post Content (Rich Text Editor)</label>
                                    <div className="prose-editor">
                                        <CKEditor
                                            editor={ClassicEditor}
                                            data={formData.long_description}
                                            onChange={(event, editor) => {
                                                const data = editor.getData();
                                                setFormData(prev => ({ ...prev, long_description: data }));
                                            }}
                                            config={{
                                                placeholder: 'Start writing your blog content here...',
                                                toolbar: [
                                                    'heading', '|',
                                                    'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', '|',
                                                    'undo', 'redo'
                                                ]
                                            }}
                                        />
                                    </div>
                                    <style>{`
                                        .prose-editor .ck-editor__editable {
                                            min-height: 300px;
                                            border-radius: 0 0 0.75rem 0.75rem !important;
                                            background-color: #f8fafc !important;
                                        }
                                        .prose-editor .ck-toolbar {
                                            border-radius: 0.75rem 0.75rem 0 0 !important;
                                            background-color: #f8fafc !important;
                                            border-bottom: none !important;
                                        }
                                        .prose-editor .ck.ck-editor__main>.ck-editor__editable:not(.ck-focused) {
                                            border-color: #e2e8f0 !important;
                                        }
                                        .prose-editor .ck.ck-editor__main>.ck-editor__editable.ck-focused {
                                            border-color: #2563eb !important;
                                            box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1) !important;
                                        }
                                    `}</style>
                                </div>

                                {/* Highlights (Array of bullet points) */}
                                <div className="md:col-span-2 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <label className="text-sm font-bold text-slate-700">Key Highlights (Bullet Points)</label>
                                        <button
                                            type="button"
                                            onClick={() => addArrayItem('highlights')}
                                            className="text-xs font-bold text-primary-600 hover:text-primary-700 flex items-center gap-1"
                                        >
                                            <PlusIcon className="h-4 w-4" /> Add Highlight
                                        </button>
                                    </div>
                                    <div className="space-y-3">
                                        {formData.highlights.map((highlight, index) => (
                                            <div key={index} className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={highlight}
                                                    onChange={(e) => handleArrayChange(index, e.target.value, 'highlights')}
                                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none text-sm"
                                                    placeholder={`Highlight ${index + 1}...`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeArrayItem(index, 'highlights')}
                                                    className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                                                >
                                                    <TrashIcon className="h-5 w-5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-4 pt-4 sticky bottom-0 bg-white py-4 border-t border-slate-50">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="flex-1 px-6 py-4 border-2 border-slate-100 text-slate-500 font-bold rounded-2xl hover:bg-slate-50 transition-all hover:border-slate-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-2 px-10 py-4 bg-primary-600 text-white font-bold rounded-2xl hover:bg-primary-700 transition-all shadow-lg shadow-primary-200 disabled:opacity-50"
                                >
                                    {loading ? 'Saving...' : post ? 'Update Post' : 'Add Post'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BlogModal;
